/**
 * Response Incident Cell tool (Page186 Soft≠Live).
 * Soft fixtures stamped soft_demo; Soft invent Live forbidden —
 * practice buttons never POST Live.
 */
import { useMemo, useState, useEffect } from "react";
import { html, Ico, Alert, CraftSelect } from "./ui.js";
import { path, useApi } from "./api.js";
import { softResponseErrorOf } from "./response-live.js";
import { responseCellSeatOf } from "./swarm-desk.js";
import { softQueueClone, softPeopleClone, softDevicesClone } from "./response-soft-demo.js";

function safeCellPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_cell_status") return "/response/cell/status";
    if (op === "get__incidents") {
      const lim = (opts && opts.query && opts.query.limit) || 40;
      return `/incidents?limit=${encodeURIComponent(lim)}`;
    }
    return "";
  }
}

const now = () => Date.now();

/** Soft≠Live: practice packs for UI verification — never POST Live. */
export const SOFT_CELL_SCENARIOS = [
  {
    id: "soft-idle",
    soft_demo: true,
    label: "Idle yard",
    blurb: "No cell open — empty catalog.",
    status: "idle",
    device_id: "",
    priority: "normal",
    cell: null,
    roles: [],
    members: [],
    trail: [],
    audit: [],
  },
  {
    id: "soft-forming",
    soft_demo: true,
    label: "Forming · West Yard",
    blurb: "Matches Queue soft-inc-p4-yard · roles still empty.",
    status: "forming",
    device_id: "yard-west",
    incident: "soft-inc-p4-yard",
    priority: "elevated",
    cell: { cell_id: "cell-soft-forming", state: "active", risk_score: 62 },
    roles: [
      { role: "scout", resource_id: "", state: "open" },
      { role: "perimeter", resource_id: "", state: "open" },
      { role: "responder", resource_id: "", state: "open" },
      { role: "command", resource_id: "", state: "open" },
    ],
    members: [],
    trail: [
      { trace_type: "escalation_needed", actor: "soft·system", created_at_ms: now() - 90000 },
    ],
    audit: [
      { action: "cell.activate", actor: "soft·desk", created_at_ms: now() - 120000 },
    ],
  },
  {
    id: "soft-active",
    soft_demo: true,
    label: "Active team · East Dock",
    blurb: "Matches Queue soft-inc-p2-dock · Omar en route.",
    status: "active",
    device_id: "dock-east-02",
    incident: "soft-inc-p2-dock",
    priority: "elevated",
    cell: { cell_id: "cell-soft-dock", state: "active", risk_score: 71 },
    roles: [
      { role: "scout", resource_id: "chen.wei", state: "filled", fit: 0.91 },
      { role: "perimeter", resource_id: "nina.cho", state: "filled", fit: 0.84 },
      { role: "responder", resource_id: "omar.hassan", state: "filled", fit: 0.88 },
      { role: "recorder", resource_id: "dock-east-02", state: "filled" },
      { role: "command", resource_id: "aisha.rahman", state: "filled" },
    ],
    members: [
      { resource_id: "chen.wei", role: "scout", state: "on_station" },
      { resource_id: "nina.cho", role: "perimeter", state: "enroute" },
      { resource_id: "omar.hassan", role: "responder", state: "on_scene" },
      { resource_id: "dock-east-02", role: "recorder", state: "live" },
    ],
    trail: [
      { trace_type: "scout_arrived", actor: "chen.wei", created_at_ms: now() - 240000 },
      { trace_type: "perimeter_secured", actor: "nina.cho", created_at_ms: now() - 90000 },
      { trace_type: "subject_located", actor: "omar.hassan", created_at_ms: now() - 40000 },
    ],
    audit: [
      { action: "cell.activate", actor: "soft·desk", created_at_ms: now() - 400000 },
      { action: "cluster.join", actor: "chen.wei", created_at_ms: now() - 300000 },
      { action: "cluster.join", actor: "omar.hassan", created_at_ms: now() - 180000 },
    ],
  },
  {
    id: "soft-critical",
    soft_demo: true,
    label: "Critical · North Gate",
    blurb: "Matches Queue soft-inc-p1-gate · high risk.",
    status: "critical",
    device_id: "gate-north-04",
    incident: "soft-inc-p1-gate",
    priority: "critical",
    cell: { cell_id: "cell-soft-crit", state: "active", risk_score: 94 },
    roles: [
      { role: "scout", resource_id: "chen.wei", state: "filled" },
      { role: "responder", resource_id: "", state: "open" },
      { role: "command", resource_id: "aisha.rahman", state: "filled" },
    ],
    members: [
      { resource_id: "chen.wei", role: "scout", state: "on_scene" },
      { resource_id: "aisha.rahman", role: "command", state: "coordinating" },
    ],
    trail: [
      { trace_type: "scout_arrived", actor: "chen.wei", created_at_ms: now() - 60000 },
      { trace_type: "escalation_needed", actor: "chen.wei", created_at_ms: now() - 20000 },
    ],
    audit: [
      { action: "cell.activate", actor: "soft·auto", created_at_ms: now() - 90000 },
      { action: "priority.set", actor: "soft·desk", created_at_ms: now() - 50000 },
    ],
  },
  {
    id: "soft-thin",
    soft_demo: true,
    label: "Thin team · Plant B",
    blurb: "Matches soft-inc-p2-plant · Maya only.",
    status: "active",
    device_id: "plant-b-12",
    incident: "soft-inc-p2-plant",
    priority: "normal",
    cell: { cell_id: "cell-soft-thin", state: "active", risk_score: 48 },
    roles: [
      { role: "responder", resource_id: "maya.ortiz", state: "filled" },
      { role: "scout", resource_id: "", state: "open" },
      { role: "perimeter", resource_id: "", state: "open" },
    ],
    members: [
      { resource_id: "maya.ortiz", role: "responder", state: "enroute" },
    ],
    trail: [
      { trace_type: "scout_arrived", actor: "maya.ortiz", created_at_ms: now() - 30000 },
    ],
    audit: [
      { action: "cluster.join", actor: "maya.ortiz", created_at_ms: now() - 45000 },
    ],
  },
  {
    id: "soft-closing",
    soft_demo: true,
    label: "Closing · Stair C",
    blurb: "Matches soft-inc-resolved · all_clear.",
    status: "closing",
    device_id: "stair-c",
    incident: "soft-inc-resolved",
    priority: "normal",
    cell: { cell_id: "cell-soft-close", state: "active", risk_score: 18 },
    roles: [
      { role: "responder", resource_id: "luis.park", state: "filled" },
      { role: "command", resource_id: "aisha.rahman", state: "filled" },
    ],
    members: [
      { resource_id: "luis.park", role: "responder", state: "standing_down" },
    ],
    trail: [
      { trace_type: "subject_located", actor: "luis.park", created_at_ms: now() - 200000 },
      { trace_type: "all_clear", actor: "luis.park", created_at_ms: now() - 15000 },
    ],
    audit: [
      { action: "trail.all_clear", actor: "luis.park", created_at_ms: now() - 15000 },
    ],
  },
  {
    id: "soft-intrusion",
    soft_demo: true,
    label: "Intrusion · Main Lobby",
    blurb: "Matches soft-inc-p3-lobby · Aisha on scene.",
    status: "active",
    device_id: "lobby-main",
    incident: "soft-inc-p3-lobby",
    priority: "intrusion",
    cell: { cell_id: "cell-soft-fence", state: "active", risk_score: 81 },
    roles: [
      { role: "perimeter", resource_id: "chen.wei", state: "filled" },
      { role: "recorder", resource_id: "lobby-main", state: "filled" },
      { role: "responder", resource_id: "aisha.rahman", state: "filled" },
    ],
    members: [
      { resource_id: "chen.wei", role: "perimeter", state: "on_station" },
      { resource_id: "lobby-main", role: "recorder", state: "live" },
      { resource_id: "aisha.rahman", role: "responder", state: "enroute" },
    ],
    trail: [
      { trace_type: "perimeter_secured", actor: "chen.wei", created_at_ms: now() - 70000 },
      { trace_type: "escalation_needed", actor: "soft·ai", created_at_ms: now() - 25000 },
    ],
    audit: [
      { action: "cell.activate", actor: "soft·ai", created_at_ms: now() - 110000 },
      { action: "cluster.join", actor: "chen.wei", created_at_ms: now() - 95000 },
    ],
  },
];

export function softCellScenarioOf(id) {
  const hit = SOFT_CELL_SCENARIOS.find((s) => s.id === id);
  return hit ? structuredCloneSoft(hit) : structuredCloneSoft(SOFT_CELL_SCENARIOS[0]);
}

function structuredCloneSoft(row) {
  return JSON.parse(JSON.stringify(row));
}

const TRACE_OPTS = [
  { value: "scout_arrived", label: "Scout arrived", hint: "Unit on station" },
  { value: "perimeter_secured", label: "Perimeter secured", hint: "Ring held" },
  { value: "subject_located", label: "Subject located", hint: "Visual / contact" },
  { value: "escalation_needed", label: "Escalation needed", hint: "Raise priority" },
  { value: "all_clear", label: "All clear", hint: "Stand down" },
];

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "unit", label: "Unit" },
  { id: "team", label: "Team" },
  { id: "trail", label: "Trail" },
];

function softResourceLabel(resourceId, people, devices) {
  const rid = String(resourceId || "");
  if (!rid) return "unfilled";
  const person = people.find((p) => p.user === rid || p.name === rid);
  if (person) return person.name || rid;
  const device = devices.find((d) => String(d.device_id || d.id) === rid);
  if (device) return device.display_label || device.label || rid;
  return rid;
}

function enrichSoftFocus(focus) {
  if (!focus) return null;
  const queues = softQueueClone();
  const people = softPeopleClone();
  const devices = softDevicesClone();
  const q = queues.find((r) => r.incident === focus.incident) || null;
  const dev = devices.find((d) => String(d.device_id || d.id) === String(focus.device_id || "")) || null;
  const whoName = (q && q.who && q.who !== "—") ? q.who : "";
  const person = whoName
    ? people.find((p) => String(p.name).toLowerCase() === whoName.toLowerCase()
      || String(p.user).toLowerCase() === whoName.toLowerCase())
    : null;
  const memberPeople = (focus.members || []).map((m) => {
    const hit = people.find((p) => p.user === m.resource_id || p.name === m.resource_id);
    const cam = !hit
      ? devices.find((d) => String(d.device_id || d.id) === String(m.resource_id || ""))
      : null;
    return {
      ...m,
      label: (hit && hit.name) || (cam && (cam.display_label || cam.label)) || m.resource_id,
      phoneSay: hit
        ? ((hit.phoneSay) || hit.phone || "No phone on file")
        : (cam ? "Device · no phone" : "No phone on file"),
      onCall: hit ? !!hit.on : null,
    };
  });
  const rolesEnriched = (focus.roles || []).map((a) => ({
    ...a,
    label: softResourceLabel(a.resource_id, people, devices),
  }));
  return {
    ...focus,
    zone: focus.zone || (q && q.zone) || "—",
    reason: focus.reason || (q && q.reason) || focus.blurb || "—",
    alarm: focus.alarm || (q && q.correlation) || "—",
    risk: (focus.cell && focus.cell.risk_score != null)
      ? focus.cell.risk_score
      : (q && q.risk != null ? q.risk : "—"),
    lead: whoName || (person && person.name) || "—",
    leadUser: (person && person.user) || "—",
    leadPhone: (person && (person.phoneSay || person.phone)) || "No phone on file",
    callsign: (q && q.callsign) || "—",
    channel: (q && q.channel) || "—",
    deviceLabel: (dev && (dev.display_label || dev.label)) || focus.device_id || "no device",
    km: (q && q.km) || "—",
    etaMin: q && q.etaMin != null ? q.etaMin : null,
    memberPeople,
    rolesEnriched,
  };
}

function ageSay(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const s = Math.max(0, Math.round((Date.now() - n) / 1000));
  if (s < 60) return s + "s ago";
  if (s < 3600) return Math.round(s / 60) + "m ago";
  return Math.round(s / 3600) + "h ago";
}

export function Cell({ toast }) {
  const posture = useApi(safeCellPath("get__response_cell_status"));
  const seat = responseCellSeatOf(posture.loading ? null : (posture.data || {}));
  const liveInc = useApi(
    seat.incidentsListAllowed
      ? safeCellPath("get__incidents", { query: { limit: seat.incidentsLimit || 40 } })
      : "",
  );
  const liveRows = useMemo(() => {
    const raw = (liveInc.data && (liveInc.data.items || liveInc.data.incidents)) || [];
    return Array.isArray(raw) ? raw.slice(0, seat.incidentsLimit || 40) : [];
  }, [liveInc.data, seat.incidentsLimit]);

  const [forceSoft, setForceSoft] = useState(false);
  const [packs, setPacks] = useState(() => SOFT_CELL_SCENARIOS.map(structuredCloneSoft));
  const [pickId, setPickId] = useState("soft-active");
  const [tab, setTab] = useState("overview");
  const [traceType, setTraceType] = useState(TRACE_OPTS[0].value);

  const useSoft = forceSoft;
  const focusRaw = useMemo(
    () => packs.find((p) => p.id === pickId) || packs[0] || null,
    [packs, pickId],
  );
  const focus = useMemo(() => enrichSoftFocus(focusRaw), [focusRaw]);

  useEffect(() => {
    if (!useSoft && liveRows.length && forceSoft === false) {
      /* Live catalog paint — Soft invent forbidden. */
    }
  }, [useSoft, liveRows.length, forceSoft]);

  const softMutate = (fn) => {
    setPacks((rows) => rows.map((r) => (r.id === pickId ? fn(structuredCloneSoft(r)) : r)));
  };

  const softActivate = () => {
    if (!focus || !focus.soft_demo) return;
    softMutate((r) => ({
      ...r,
      status: "forming",
      cell: r.cell || { cell_id: "cell-" + r.id, state: "active", risk_score: 55 },
      roles: r.roles.length
        ? r.roles
        : [
            { role: "scout", resource_id: "", state: "open" },
            { role: "responder", resource_id: "", state: "open" },
            { role: "command", resource_id: "", state: "open" },
          ],
      audit: [
        { action: "cell.activate", actor: "soft·desk", created_at_ms: Date.now() },
        ...(r.audit || []),
      ],
    }));
    toast && toast("Practice cell opened", "warn");
  };

  const softClose = () => {
    if (!focus || !focus.soft_demo) return;
    softMutate((r) => ({
      ...r,
      status: "closed",
      cell: r.cell ? { ...r.cell, state: "closed", risk_score: 0 } : null,
      audit: [
        { action: "cell.resolve", actor: "soft·desk", created_at_ms: Date.now() },
        ...(r.audit || []),
      ],
    }));
    toast && toast("Soft close · cell only · not Incident SoR", "warn");
  };

  const softTrail = () => {
    if (!focus || !focus.soft_demo) return;
    softMutate((r) => ({
      ...r,
      trail: [
        { trace_type: traceType, actor: "soft·desk", created_at_ms: Date.now() },
        ...(r.trail || []),
      ],
      audit: [
        { action: "trail." + traceType, actor: "soft·desk", created_at_ms: Date.now() },
        ...(r.audit || []),
      ],
    }));
    toast && toast("Practice trail recorded · " + traceType, "warn");
  };

  const resetSoft = () => {
    setPacks(SOFT_CELL_SCENARIOS.map(structuredCloneSoft));
    setPickId("soft-active");
    setForceSoft(true);
    toast && toast("Soft scenarios reset", "go");
  };

  const openRoles = (focus && focus.roles ? focus.roles.filter((a) => a.state === "open") : []).length;
  const filledRoles = (focus && focus.roles ? focus.roles.filter((a) => a.state === "filled") : []).length;

  return html`
    <div className="rr-cell" data-cmc-residual="page186-cell" data-soft=${useSoft ? "1" : "0"}>
      <p className="rr-cell__lede">
        ${useSoft
          ? "Practice cell. Pick a scenario, open Overview for the full sheet, then Unit / Team / Trail. Practice buttons never write Live."
          : "Live incident catalog. Practice buttons stay off this list."}
      </p>

      ${posture.error
        ? html`<${Alert} tone="warn" title="Could not confirm cell seat" say=${softResponseErrorOf(posture.error)}/>`
        : null}

      <div className="rr-cell__mode">
        <button type="button" data-on=${useSoft ? "1" : "0"}
          onClick=${() => { setForceSoft(true); toast && toast("Soft practice on", "go"); }}>
          Soft practice
        </button>
        <button type="button" data-on=${!useSoft ? "1" : "0"}
          disabled=${!seat.incidentsListAllowed}
          onClick=${() => {
            if (!seat.incidentsListAllowed) {
              toast && toast("Live catalog sealed for this seat", "warn");
              return;
            }
            setForceSoft(false);
            toast && toast(liveRows.length ? ("Live catalog · " + liveRows.length) : "Live catalog empty", "go");
          }}>
          Live catalog (${liveRows.length})
        </button>
        <button type="button" className="btn btn--text btn--sm" onClick=${resetSoft}>Reset Soft</button>
      </div>

      ${useSoft ? html`
        <div className="rr-cell__scenarios" role="listbox" aria-label="Soft cell scenarios">
          ${packs.map((s) => html`
            <button key=${s.id} type="button" role="option"
              aria-selected=${pickId === s.id ? "true" : "false"}
              data-on=${pickId === s.id ? "1" : "0"}
              data-status=${s.status}
              onClick=${() => setPickId(s.id)}>
              <b>${s.label}</b>
              <em>${s.blurb}</em>
            </button>`)}
        </div>
      ` : html`
        <div className="rr-cell__live-list">
          ${!seat.incidentsListAllowed
            ? html`<p className="note">Live incidents sealed.</p>`
            : liveInc.loading
              ? html`<p className="note">Loading Live…</p>`
              : !liveRows.length
                ? html`<p className="note">No live incidents — switch to Soft practice to preview the sheet.</p>`
                : liveRows.map((r) => {
                  const id = String(r.incident_id || r.id || "");
                  const st = r.status || r.state || "—";
                  const pri = r.priority || r.severity || "";
                  const zone = r.zone || r.site || "";
                  return html`<button key=${id} type="button" className="rr-cell__live-row"
                    onClick=${() => toast && toast("Open the full Incident cell for live detail. This float is the practice sheet.", "warn")}>
                    <b>${id}</b>
                    <em>${r.device_id || "no device"}${zone ? " · " + zone : ""} · ${st}${pri ? " · " + pri : ""}</em>
                  </button>`;
                })}
        </div>
      `}

      ${useSoft && focus ? html`
        <header className="rr-cell__focus">
          <div>
            <span>Selected Soft cell</span>
            <strong>${focus.label}</strong>
            <em>${focus.deviceLabel} · ${focus.incident || "—"}</em>
            <em>${focus.zone} · ${focus.priority} · ${focus.status}</em>
          </div>
          <div className="rr-cell__badges">
            <span className="chip" data-tone="warn">soft_demo</span>
            <span className="chip">${filledRoles} filled</span>
            <span className="chip" data-tone=${openRoles ? "warn" : ""}>${openRoles} open roles</span>
          </div>
        </header>

        <div className="rr-cell__tabs" role="tablist">
          ${TABS.map((t) => html`
            <button key=${t.id} type="button" role="tab"
              aria-selected=${tab === t.id ? "true" : "false"}
              data-on=${tab === t.id ? "1" : "0"}
              onClick=${() => setTab(t.id)}>${t.label}</button>`)}
        </div>

        ${tab === "overview" ? html`
          <section className="rr-cell__pane">
            <div className="rr-cell__detail" data-open="1">
              ${[
                ["Incident", focus.incident || "—"],
                ["Label", focus.label],
                ["Status", focus.status],
                ["Priority", focus.priority],
                ["Zone", focus.zone],
                ["Reason", focus.reason],
                ["Alarm / correlator", focus.alarm],
                ["Risk", String(focus.risk)],
                ["Device", focus.deviceLabel],
                ["Device id", focus.device_id || "—"],
                ["Lead", focus.lead],
                ["Lead username", focus.leadUser],
                ["Lead phone", focus.leadPhone],
                ["Callsign", focus.callsign],
                ["Channel", focus.channel],
                ["Distance", focus.km !== "—" ? (focus.km + " km") : "—"],
                ["ETA", focus.etaMin == null ? "—" : (focus.etaMin === 0 ? "on site" : (focus.etaMin + " min"))],
                ["Cell id", (focus.cell && focus.cell.cell_id) || "—"],
                ["Cell state", (focus.cell && focus.cell.state) || "none"],
                ["Roles filled", filledRoles + " / " + ((focus.roles || []).length || 0)],
                ["Members on muster", String((focus.members || []).length)],
                ["Trail marks", String((focus.trail || []).length)],
                ["Audit rows", String((focus.audit || []).length)],
                ["Source", "Practice scenario"],
              ].map(([k, v]) => html`
                <div key=${k} className="rr-cell__field">
                  <u>${k}</u>
                  <b>${v}</b>
                </div>`)}
            </div>
            <div className="rr-cell__acts">
              <button type="button" className="btn btn--go btn--sm" onClick=${softActivate}
                disabled=${focus.status === "closed"}>Soft activate</button>
              <button type="button" className="btn btn--danger btn--sm" onClick=${softClose}
                disabled=${!focus.cell || focus.status === "closed"}>Soft close</button>
            </div>
          </section>
        ` : null}

        ${tab === "unit" ? html`
          <section className="rr-cell__pane">
            <p className="rr-cell__summary">
              ${focus.cell
                ? html`<b>${focus.cell.state}</b> · risk ${focus.cell.risk_score} · ${focus.cell.cell_id}`
                : html`<span>No cell yet — Soft activate to open one.</span>`}
            </p>
            <div className="rr-cell__detail">
              ${[
                ["Incident", focus.incident || "—"],
                ["Device", focus.deviceLabel],
                ["Zone", focus.zone],
                ["Priority", focus.priority],
              ].map(([k, v]) => html`
                <div key=${k} className="rr-cell__field"><u>${k}</u><b>${v}</b></div>`)}
            </div>
            <div className="rr-cell__roles">
              ${(focus.rolesEnriched || focus.roles || []).length
                ? (focus.rolesEnriched || focus.roles).map((a) => html`
                    <div key=${a.role} className="rr-cell__role" data-state=${a.state}>
                      <b>${a.role}</b>
                      <em>${a.label || a.resource_id || "unfilled"}${a.resource_id && a.label && a.label !== a.resource_id ? " · " + a.resource_id : ""}</em>
                      <span>${a.state}${a.fit != null ? " · fit " + a.fit : ""}</span>
                    </div>`)
                : html`<p className="note">No roles allocated.</p>`}
            </div>
            <div className="rr-cell__acts">
              <button type="button" className="btn btn--go btn--sm" onClick=${softActivate}
                disabled=${focus.status === "closed"}>Soft activate</button>
              <button type="button" className="btn btn--danger btn--sm" onClick=${softClose}
                disabled=${!focus.cell || focus.status === "closed"}>Soft close</button>
            </div>
          </section>
        ` : null}

        ${tab === "team" ? html`
          <section className="rr-cell__pane">
            <p className="rr-cell__summary">Priority <b>${focus.priority}</b> · ${(focus.memberPeople || []).length} on muster</p>
            <div className="rr-cell__members">
              ${(focus.memberPeople || []).length
                ? focus.memberPeople.map((m) => html`
                    <div key=${m.resource_id} className="rr-cell__member">
                      <div className="rr-cell__member-main">
                        <b>${m.label || m.resource_id}</b>
                        <em>${m.resource_id} · ${m.role}</em>
                        <strong className="rr-cell__phone" data-empty=${m.phoneSay && m.phoneSay !== "No phone on file" ? "0" : "1"}>
                          ${m.phoneSay || "No phone on file"}
                        </strong>
                      </div>
                      <span>${m.state}${m.onCall === true ? " · on call" : (m.onCall === false ? " · off call" : "")}</span>
                    </div>`)
                : html`<p className="note">No members yet — try Active team or Intrusion Soft packs.</p>`}
            </div>
          </section>
        ` : null}

        ${tab === "trail" ? html`
          <section className="rr-cell__pane">
            <div className="rr-cell__trail-tools">
              <label className="rr-cell__field-sel">
                <span>Trail mark</span>
                <${CraftSelect}
                  drop="up"
                  aria-label="Soft trail mark"
                  value=${traceType}
                  placeholder="Choose mark…"
                  options=${TRACE_OPTS}
                  onChange=${(e) => setTraceType(e.target.value)}/>
              </label>
              <button type="button" className="btn btn--go btn--sm" onClick=${softTrail}>Soft trail mark</button>
            </div>
            <div className="rr-cell__feed">
              <h4>Trail</h4>
              ${(focus.trail || []).length
                ? focus.trail.map((t, i) => html`
                    <div key=${(t.trace_type || "") + i} className="rr-cell__feed-row">
                      <b>${t.trace_type}</b>
                      <em>${t.actor} · ${ageSay(t.created_at_ms)}</em>
                    </div>`)
                : html`<p className="note">No trail marks.</p>`}
              <h4>Audit</h4>
              ${(focus.audit || []).length
                ? focus.audit.map((a, i) => html`
                    <div key=${(a.action || "") + i} className="rr-cell__feed-row">
                      <b>${a.action}</b>
                      <em>${a.actor} · ${ageSay(a.created_at_ms)}</em>
                    </div>`)
                : html`<p className="note">No audit rows.</p>`}
            </div>
          </section>
        ` : null}
      ` : null}

      <footer className="rr-cell__foot">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed && !posture.loading}
          onClick=${() => {
            posture.reload && posture.reload();
            liveInc.reload && liveInc.reload();
            toast && toast("Cell seat refreshed", "go");
          }}>
          <${Ico} d="refresh" w=${13}/> Refresh seat
        </button>
        <span className="rr-cell__note">Practice buttons never write a live cell.</span>
      </footer>
    </div>
  `;
}
